import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

export interface AdminCustomerUserRow {
  id: string;
  customer_id: string;
  full_name: string | null;
  role: string;
  default_location_id: string | null;
  phone: string | null;
  active: boolean;
  is_superadmin: boolean;
  created_at: string;
  email: string | null;
  last_sign_in_at: string | null;
}

/**
 * Users on one customer. `includeRemoved` also returns detached memberships
 * (`active: false`) — a detached user is hidden, never deleted, and without
 * this there was no way to see or restore one.
 */
export function useAdminCustomerUsers(customerId: string | null, includeRemoved = false) {
  return useQuery<{ ok: true; count: number; rows: AdminCustomerUserRow[] }>({
    queryKey: ['admin', 'customer-users', customerId, includeRemoved],
    enabled: !!customerId,
    queryFn: () =>
      adminFetch(
        `admin-list-customer-users?customer_id=${customerId!}` +
          (includeRemoved ? '&include_removed=1' : ''),
      ),
    staleTime: 30 * 1000,
  });
}

/** Change a user's email (their login) or name, in place. */
export function useUpdateCustomerUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      customer_id: string;
      user_id: string;
      email?: string;
      full_name?: string;
      phone?: string | null;
    }) =>
      adminFetch<{
        ok: true;
        user_id: string;
        email: string | null;
        email_changed: boolean;
        old_email?: string;
      }>('admin-update-customer-user', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-users', v.customer_id] });
    },
  });
}

export interface CreateCustomerUserInput {
  customer_id: string;
  email: string;
  password: string;
  full_name: string;
  phone?: string;
  role: MembershipRole;
  default_location_id?: string | null;
  send_welcome?: boolean;
}

export function useCreateCustomerUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerUserInput) =>
      adminFetch<{
        ok: true;
        user_id: string;
        email: string;
        email_sent: boolean;
        email_warning: string | null;
      }>('admin-create-customer-user', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-users', variables.customer_id] });
    },
  });
}

export function useSendWelcomeEmail() {
  return useMutation({
    mutationFn: (input: { user_id: string; password?: string }) =>
      adminFetch<{ ok: true; email: string; password_reset: boolean }>(
        'admin-send-welcome-email',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
  });
}

export interface AttachUserInput {
  email: string;
  customer_id: string;
  role: MembershipRole;
  default_location_id?: string | null;
}

export function useAttachUserToCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AttachUserInput) =>
      adminFetch<{
        ok: true;
        user_id: string;
        email: string;
        customer_id: string;
        action: 'created' | 'reactivated' | 'already_active';
      }>('admin-attach-user-to-customer', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-users', variables.customer_id] });
    },
  });
}

export function useDetachUserFromCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { user_id: string; customer_id: string }) =>
      adminFetch<{ ok: true; user_id: string; customer_id: string }>(
        'admin-detach-user-from-customer',
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-users', variables.customer_id] });
    },
  });
}

export type MembershipRole = 'admin' | 'member' | 'ap';

export function useSetMembershipRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      user_id: string;
      customer_id: string;
      role: MembershipRole;
    }) =>
      adminFetch<{
        ok: true;
        user_id: string;
        customer_id: string;
        from?: string;
        to?: string;
        unchanged?: boolean;
      }>('admin-set-membership-role', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'customer-users', variables.customer_id] });
    },
  });
}
